/**
 * pwaInstall.js — port of `_migration_source/frontend/src/lib/pwaInstall.ts`.
 *
 * Captures the `beforeinstallprompt` event at module load (fires ONCE per
 * session, so we can't lazy-mount a listener), detects the runtime device +
 * browser, and exposes a `usePwaInstall()` hook mirroring the source.
 *
 * Migration Principle: Platform adaptation only. Same public API as source
 * (`{ device, browser, installed, canPromptInstall, promptInstall }`) —
 * React Native `Platform.OS` guard is dropped since we're only on web.
 */
import { useCallback, useEffect, useState } from "react";

let _deferredPrompt = null;
let _listenerInstalled = false;
const _subscribers = [];

function _notify() {
  _subscribers.forEach((cb) => {
    try { cb(); } catch { /* swallow */ }
  });
}

if (typeof window !== "undefined" && !_listenerInstalled) {
  _listenerInstalled = true;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    _deferredPrompt = e;
    _notify();
  });
  window.addEventListener("appinstalled", () => {
    _deferredPrompt = null;
    _notify();
  });
}

export function detectDevice() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  // iPadOS 13+ reports MacIntel + touch — treat as iOS.
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

export function detectBrowser() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) {
    if (/CriOS\//.test(ua)) return "chrome-ios";
    return "safari-ios";
  }
  if (/Android/i.test(ua)) {
    if (/SamsungBrowser/i.test(ua)) return "samsung-internet";
    if (/Chrome\//.test(ua)) return "chrome-android";
  }
  if (/Edg\//.test(ua)) return "edge-desktop";
  if (/Chrome\//.test(ua)) return "chrome-desktop";
  if (/Firefox\//.test(ua)) return "firefox";
  if (/Safari\//.test(ua)) return "safari-desktop";
  return "unknown";
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
    if (window.navigator && window.navigator.standalone === true) return true;
  } catch { /* ignore */ }
  return false;
}

export function usePwaInstall() {
  const [hasPrompt, setHasPrompt] = useState(!!_deferredPrompt);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const cb = () => {
      setHasPrompt(!!_deferredPrompt);
      setInstalled(isStandalone());
    };
    _subscribers.push(cb);
    return () => {
      const i = _subscribers.indexOf(cb);
      if (i >= 0) _subscribers.splice(i, 1);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!_deferredPrompt) return "unavailable";
    try {
      await _deferredPrompt.prompt();
      const { outcome } = await _deferredPrompt.userChoice;
      _deferredPrompt = null;
      _notify();
      return outcome;
    } catch {
      return "unavailable";
    }
  }, []);

  return {
    device: detectDevice(),
    browser: detectBrowser(),
    installed,
    canPromptInstall: hasPrompt,
    promptInstall,
  };
}
